import type React from 'react';
import {useEffect, useRef} from 'react';
import {Box} from '@mui/material';
import {BtnGroupTabs, type BtnGroupTabItem} from '~/components/BtnGroupTabs/BtnGroupTabs';

/**
 * The profile tabs on one row: when they do not fit, the row scrolls sideways (wheel, trackpad or swipe) instead of
 * wrapping, and the open tab is scrolled into view.
 */
export const ScrollableTabs: React.FC<{items: BtnGroupTabItem[]; value: string; ariaLabel?: string}> = ({
    items, value, ariaLabel = 'Category Profile tabs',
}) => {
    const row = useRef<HTMLDivElement>(null);

    // Centre the open tab; again when the labels change width (counts arrive after the first render).
    const labels = items.map(i => i.title).join('|');
    useEffect(() => {
        const el = row.current;
        const tab = el?.querySelector<HTMLElement>('.MuiButton-contained');
        if (!el || !tab) return;
        const box = el.getBoundingClientRect();
        const t = tab.getBoundingClientRect();
        el.scrollLeft += t.left - box.left - (el.clientWidth - t.width) / 2;
    }, [value, labels]);

    return (
        <Box ref={row} role="navigation" aria-label={ariaLabel}
             sx={{
                 overflowX: 'auto', overflowY: 'hidden', overscrollBehaviorX: 'contain', pb: 0.5, mb: -0.5,
                 scrollbarWidth: 'thin',
                 '&::-webkit-scrollbar': {height: 4},
                 '&::-webkit-scrollbar-thumb': {bgcolor: 'divider', borderRadius: 2},
             }}>
            <BtnGroupTabs items={items} value={value}
                          sx={{flexWrap: 'nowrap', width: 'max-content', '& > .MuiButton-root': {flexShrink: 0, whiteSpace: 'nowrap'}}}/>
        </Box>
    );
};
